import { Box, Grid, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { UserProfile } from '../../../../../../corelogic/domain/models/user.model';

// Style objects
const styles = {
  container: {
    borderRadius: 2,
    height: '100%',
    width: '100%',
    overflowY: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: { xs: 1, sm: 2 },
    marginTop: 0,
    padding: { xs: '0 4px', sm: 0 },
  },
  containerVariants: {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  },
  sectionTitle: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: { xs: '0.9rem', sm: 'clamp(0.8rem, 1.2vw, 1.1rem)' },
    marginBottom: 2,
    marginTop: 0,
    textAlign: { xs: 'center', sm: 'left' },
    width: '100%',
    padding: { xs: '0 4px', sm: 0 },
  },
  cardContainer: {
    perspective: '1000px',
    cursor: 'pointer',
    height: '100%',
    minHeight: { xs: 90, sm: 90, md: 130, lg: 160, xl: 180 },
    maxHeight: { xs: 110, sm: 120, md: 170, lg: 200, xl: 220 },
    position: 'relative',
    transformStyle: 'preserve-3d',
  },
  cardInner: (isFlipped: boolean) => ({
    position: 'absolute',
    width: '100%',
    height: '100%',
    transition: 'transform 0.6s',
    transformStyle: 'preserve-3d',
    transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
  }),
  card: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    backgroundColor: 'rgba(20, 20, 20, 0.95)',
    borderRadius: 2,
    p: { xs: 2, sm: 1.5, md: 2, lg: 2.5 },
    border: '2px solid transparent',
    background: 'rgba(20, 20, 20, 0.9)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',

    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '8px',
      padding: '2px',
      background: 'linear-gradient(45deg, #cc0000, transparent)',
      mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
  },
  cardFront: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: '100%',
    gap: { xs: 0, sm: 0.5 },
  },
  cardBack: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'rotateY(180deg)',
    padding: { xs: 2, sm: 1 },
  },
  cardValueContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    marginTop: { xs: 0, sm: 'auto' },
  },
  cardValue: {
    fontSize: { xs: '1rem', sm: 'clamp(1rem, 0.8rem + 1.2vw, 1.6rem)' },
    fontFamily: 'Rubik Mono One',
    fontWeight: 'bold',
    color: '#ffffff',
    mb: { xs: 0, sm: 1 },
  },
  cardTitle: {
    fontSize: { xs: '0.6rem', sm: 'clamp(0.5rem, 0.3rem + 0.6vw, 0.8rem)' },
    fontFamily: 'Rubik Mono One',
    letterSpacing: { xs: 0, sm: 1 },
    textAlign: 'center',
    color: '#cccccc',
    wordWrap: 'break-word',
    maxWidth: '100%',
    mb: { xs: 0, sm: 0.5 },
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: { xs: '20px', sm: '32px' },
    height: { xs: '20px', sm: '32px' },
    borderRadius: '4px',
    color: 'rgba(164, 159, 159, 0.7)',
    fontSize: { xs: '1rem', sm: '1.5rem' },
    position: 'absolute',
    top: { xs: '4px', sm: '8px' },
    left: { xs: '4px', sm: '8px' },
  },
  cardDescription: {
    fontSize: { xs: '0.7rem', sm: 'clamp(0.6rem, 0.4rem + 0.8vw, 0.9rem)' },
    fontFamily: 'Arial',
    color: '#ffffff',
    textAlign: 'center',
  },
};

interface ProfileStatsProps {
  user: UserProfile;
}

const StatsCard = ({
  titleLine1,
  titleLine2,
  value,
  description,
  icon,
}: {
  titleLine1: string;
  titleLine2: string;
  value: string;
  description: string;
  icon: string;
}) => {
  const [isFlipped, setIsFlipped] = useState(false);

  return (
    <Box onClick={() => setIsFlipped(!isFlipped)} sx={styles.cardContainer}>
      <Box sx={styles.cardInner(isFlipped)}>
        {/* Front Face */}
        <Box sx={{ ...styles.card, ...styles.cardFront }}>
          <Typography sx={styles.cardTitle}>{titleLine1}</Typography>
          <Typography sx={styles.cardTitle}>{titleLine2}</Typography>
          <Typography sx={styles.iconContainer}>{icon}</Typography>

          <Box sx={styles.cardValueContainer}>
            <Typography sx={styles.cardValue}>{value}</Typography>
          </Box>
        </Box>

        {/* Back Face */}
        <Box sx={{ ...styles.card, ...styles.cardBack }}>
          <Typography sx={styles.cardDescription}>{description}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ProfileStats = ({ user }: ProfileStatsProps) => {
  // Stats card data
  const statsCards = [
    {
      titleLine1: 'Active',
      titleLine2: 'Seasons',
      value: 'N/A', // TODO: get this from the userSeason Later
      description: 'Seasons where you have already selected fighters',
      icon: '🥊',
    },
    {
      titleLine1: 'Finished',
      titleLine2: 'Seasons',
      value: user?.seasonsCompleted?.toString() || '0',
      description: 'Total number of seasons you have completed',
      icon: '🎯',
    },
    {
      titleLine1: 'Season',
      titleLine2: 'Wins',
      value: user?.seasonsWon?.toString() || '0',
      description: 'Total number of seasons you have won',
      icon: '🏅',
    },
    {
      titleLine1: 'Active',
      titleLine2: 'Tournaments',
      value: 'N/A', // TODO: get this from the userSeason Later
      description: 'Number of tournaments you are currently participating in',
      icon: '🥊',
    },
    {
      titleLine1: 'Finished',
      titleLine2: 'Tournaments',
      value: user?.tournamentsCompleted?.toString() || '0',
      description: 'Total number of tournaments you have participated in',
      icon: '🎯',
    },
    {
      titleLine1: 'Tournament',
      titleLine2: 'Wins',
      value: user?.tournamentsWon?.toString() || '0',
      description: 'Total number of tournaments you have won',
      icon: '🏅',
    },
  ];

  return (
    <Box
      component={motion.div}
      initial="hidden"
      whileInView="visible"
      viewport={{ amount: 0.2 }}
      variants={styles.containerVariants}
      sx={styles.container}
    >
      <Typography sx={styles.sectionTitle}>Your MMA Fantasy Stats</Typography>

      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ width: '100%', mt: 0, mb: 0 }}
        columns={{ xs: 2, sm: 2, md: 3 }}
      >
        {statsCards.map((card, index) => (
          <Grid item xs={1} sm={1} md={1} key={index}>
            <StatsCard {...card} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ProfileStats;
